import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, KeyRound } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /settings/profile — self-service profile management (any role).
 *  - View/edit own name (PATCH /api/auth/me)
 *  - Change password (POST /api/auth/change-password)
 */
export default function Profile() {
  const { t } = useTranslation(["settings", "common"]);
  const { user, tenant, refreshMe } = useAuth();

  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState(null);

  const onSaveName = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      await refreshMe?.();
      toast.success(t("settings:profile.nameSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common:errors.unexpected"));
    } finally {
      setSavingName(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (next.length < 8) {
      setPwError("weak_password");
      return;
    }
    if (next !== confirm) {
      setPwError("password_mismatch");
      return;
    }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success(t("settings:profile.passwordSaved"));
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const key = typeof detail === "object" ? detail.detail : detail;
      setPwError(key || "unexpected");
    } finally {
      setPwLoading(false);
    }
  };

  const ROLE_TONES = {
    owner: "bg-indigo-50 text-indigo-700 border-indigo-200",
    admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
    researcher: "bg-sky-50 text-sky-700 border-sky-200",
    viewer: "bg-slate-50 text-slate-600 border-slate-200",
    client_viewer: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      <header>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          {t("settings:profile.section")}
        </span>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
          {t("settings:profile.title")}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-slate-500">{t("settings:profile.subtitle")}</p>
      </header>

      {/* Identity */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
          {t("settings:profile.identity")}
        </h2>
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-slate-400">{t("settings:profile.email")}</dt>
            <dd className="mt-0.5 text-sm text-slate-900" data-testid="profile-email">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-slate-400">{t("settings:profile.tenant")}</dt>
            <dd className="mt-0.5 text-sm text-slate-900" data-testid="profile-tenant">{tenant?.name}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-slate-400">{t("settings:profile.role")}</dt>
            <dd className="mt-0.5">
              <span data-testid="profile-role"
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${ROLE_TONES[user?.role] || ROLE_TONES.viewer}`}>
                <ShieldCheck className="h-3 w-3" strokeWidth={1.75} /> {user?.role}
              </span>
            </dd>
          </div>
        </dl>

        <form onSubmit={onSaveName} className="mt-6 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">{t("settings:profile.name")}</span>
            <input
              data-testid="profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <button
            type="submit"
            data-testid="profile-name-save"
            disabled={savingName || !name.trim() || name === user?.name}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
          >
            {savingName ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("common:save")}
          </button>
        </form>
      </section>

      {/* Change password */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
          {t("settings:profile.changePassword")}
        </h2>
        <p className="mt-1 text-xs text-slate-500">{t("settings:profile.changePasswordHint")}</p>

        <form onSubmit={onChangePassword} className="mt-4 space-y-3 max-w-md">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">{t("settings:profile.currentPassword")}</span>
            <input
              data-testid="profile-current-password"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">{t("settings:profile.newPassword")}</span>
            <input
              data-testid="profile-new-password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
            <p className="mt-1 text-[11px] text-slate-400">{t("settings:profile.passwordHint")}</p>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">{t("settings:profile.confirmPassword")}</span>
            <input
              data-testid="profile-confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </label>
          {pwError ? (
            <p data-testid="profile-password-error" role="alert"
               className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {t([`settings:profile.errors.${pwError}`, "common:errors.unexpected"])}
            </p>
          ) : null}
          <button
            type="submit"
            data-testid="profile-change-password-submit"
            disabled={pwLoading || !current || !next || !confirm}
            className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:opacity-50"
          >
            {pwLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />}
            {t("settings:profile.changePassword")}
          </button>
        </form>
      </section>
    </div>
  );
}
